/**
 * Audio Processor - Base audio processing pipeline
 * Orchestrates audio processing chain and Float32→Int16 conversion
 */

/* eslint-disable @typescript-eslint/no-parameter-properties */

import { IAudioProcessor, AudioProcessorConfig } from './types'
import { DitheringProcessor } from '../processing/DitheringProcessor'

/**
 * AudioProcessor orchestrates the audio processing pipeline
 *
 * Responsibilities:
 * - Manage processor chain (AGC, Pre-emphasis, Adaptive Gain, etc.)
 * - Route audio through processors in sequence
 * - Handle Float32→Int16 conversion with optional dithering
 * - Maintain processing state
 *
 * Processing order matters:
 * 1. AGC (normalize levels)
 * 2. Pre-emphasis (boost high frequencies)
 * 3. Adaptive Gain (dynamic control)
 * 4. Dithering (during Int16 conversion)
 */
export class AudioProcessor {
  private processors: IAudioProcessor[] = []
  private hasDithering: boolean = false

  constructor(private config: AudioProcessorConfig) {
    if (config.processors) {
      this.processors = config.processors
      // Check if dithering is in the chain
      this.hasDithering = this.processors.some(
        p => p.getName() === 'Dithering'
      )
    }
  }

  /**
   * Process audio buffer through all configured processors
   * @param inputBuffer Float32Array audio samples from Web Audio API
   * @returns Int16Array ready for WebSocket transmission
   */
  processBuffer(inputBuffer: Float32Array): Int16Array {
    let processed = inputBuffer

    // Apply each processor in sequence
    for (const processor of this.processors) {
      // Skip dithering processor (handled in convertToInt16)
      if (processor.getName() !== 'Dithering') {
        processed = processor.process(processed)
      }
    }

    // Convert to Int16 with optional dithering
    return this.convertToInt16(processed)
  }

  /**
   * Convert Float32Array to Int16Array for WebSocket transmission
   * @param buffer Processed Float32 audio samples (-1.0 to +1.0)
   * @returns Int16Array for binary WebSocket transmission
   */
  private convertToInt16(buffer: Float32Array): Int16Array {
    const int16Buffer = new Int16Array(buffer.length)

    if (this.hasDithering) {
      // Apply triangular dithering during conversion
      for (let i = 0; i < buffer.length; i++) {
        const dithered = DitheringProcessor.applyDitherToSample(buffer[i])
        // Round to nearest integer for better quantization
        int16Buffer[i] = Math.round(dithered * 0x7FFF)
      }
    } else {
      // Direct conversion without dithering
      for (let i = 0; i < buffer.length; i++) {
        const clamped = Math.max(-1.0, Math.min(1.0, buffer[i]))
        int16Buffer[i] = Math.round(clamped * 0x7FFF)
      }
    }

    return int16Buffer
  }

  /**
   * Add a processor to the end of the chain
   * @param processor Audio processor to add
   */
  addProcessor(processor: IAudioProcessor): void {
    this.processors.push(processor)
    if (processor.getName() === 'Dithering') {
      this.hasDithering = true
    }
  }

  /**
   * Remove all processors from the chain
   */
  clearProcessors(): void {
    this.processors = []
    this.hasDithering = false
  }

  /**
   * Get list of active processor names
   * @returns Array of processor names in order
   */
  getProcessorNames(): string[] {
    return this.processors.map(p => p.getName())
  }

  /**
   * Reset all processors with state
   */
  reset(): void {
    for (const processor of this.processors) {
      if (processor.reset) {
        processor.reset()
      }
    }
  }
}
